import { landingPages } from '../content/landingPages';
import { blogPosts } from '../content/blogPosts';
import { LEGAL_SLUGS } from '../content/legal';

/**
 * The bilingual public (crawlable) pages and how their Turkish and English
 * URLs map onto each other. Turkish lives at the root, English under /en/
 * (see CLAUDE.md "Bilingual URL scheme").
 *
 * Shared by the language switch (navigation.tsx) and by App.tsx, which sets
 * the content language from the URL on every public page so the rendered
 * page always matches its URL, its <html lang> and its hreflang. Without that,
 * a visitor or crawler with an English browser and no stored preference
 * (Googlebot's renderer reports en-US) saw English copy on the Turkish URLs.
 */

export type PublicLang = 'tr' | 'en';

/** Public pages whose slug is the SAME in Turkish and English. */
const SAME_SLUG_PAGES = ['/about', '/contact', '/fraud-prevention', '/blog', '/kvkk'];

/** Public pages with a distinct English slug that are not driven by a content list. */
const DISTINCT_SLUG_PAGES = [
  { slug: 'fiyatlandirma', enSlug: 'pricing' },
  { slug: 'sss', enSlug: 'faq' },
  ...Object.values(LEGAL_SLUGS),
];

/** Whether a path is under the English /en prefix. */
export function isEnglishPath(pathname: string): boolean {
  return pathname === '/en' || pathname.startsWith('/en/');
}

/**
 * Maps a URL to the same page in the target language, in its final
 * trailing-slash form. Landing pages, blog posts and legal pages have a
 * distinct English slug, resolved via their content data. Returns null for
 * routes that are not bilingual public pages (login, the authenticated app).
 */
export function localizedPath(pathname: string, lang: PublicLang): string | null {
  const clean = pathname.replace(/\/$/, '') || '/';
  const isEn = isEnglishPath(clean);
  const cur = isEn ? (clean === '/en' ? '/' : clean.slice(3)) : clean; // path without /en

  if (cur === '/' || cur === '') return lang === 'en' ? '/en/' : '/';

  for (const p of landingPages) {
    if (cur === `/${isEn ? p.enSlug : p.slug}`) {
      return lang === 'en' ? `/en/${p.enSlug}/` : `/${p.slug}/`;
    }
  }
  for (const p of DISTINCT_SLUG_PAGES) {
    if (cur === `/${isEn ? p.enSlug : p.slug}`) {
      return lang === 'en' ? `/en/${p.enSlug}/` : `/${p.slug}/`;
    }
  }
  for (const p of blogPosts) {
    if (cur === `/blog/${isEn ? p.enSlug : p.slug}`) {
      return lang === 'en' ? `/en/blog/${p.enSlug}/` : `/blog/${p.slug}/`;
    }
  }
  if (SAME_SLUG_PAGES.includes(cur)) {
    return lang === 'en' ? `/en${cur}/` : `${cur}/`;
  }
  return null;
}

/**
 * The language a public page's URL dictates, or null for non-public routes
 * (which keep the visitor's stored or browser language).
 */
export function languageForPath(pathname: string): PublicLang | null {
  if (localizedPath(pathname, 'tr') === null) return null;
  return isEnglishPath(pathname) ? 'en' : 'tr';
}
